<template>
  <div class="ccp-resultado">

    <!-- Empty state -->
    <div v-if="itemsConProducto.length === 0" class="empty-state px-8 py-6">
      <v-icon size="32" color="#BDBDBD">mdi-swap-horizontal</v-icon>
      <p class="empty-text">{{ $t("compararPreciosProveedor.noProductosMatcheados") }}</p>
    </div>

    <template v-else>

      <!-- Leyenda -->
      <div class="ccp-leyenda px-8 pt-4 pb-3">
        <div class="legend-chip legend-chip--asociar">
          <span class="legend-dot legend-dot--asociar"></span>
          {{ $t("compararPreciosProveedor.leyendaAsociar") }}
        </div>
        <div class="legend-chip legend-chip--ignorar">
          <span class="legend-dot legend-dot--ignorar"></span>
          {{ $t("compararPreciosProveedor.leyendaIgnorar") }}
        </div>
        <div class="legend-chip legend-chip--pendiente">
          <span class="legend-dot legend-dot--pendiente"></span>
          {{ $t("compararPreciosProveedor.leyendaPendiente") }}
        </div>
        <span class="legend-resumen ml-auto">
          {{ $t("compararPreciosProveedor.resumenAsociar", { n: cantidadAsociar }) }}
        </span>
      </div>

      <!-- Tabla -->
      <div class="table-wrapper">
        <div class="table-scroll">
          <table class="ccp-table" cellspacing="0" cellpadding="0">
            <thead>
              <tr>
                <th class="cth cth-star"></th>
                <th class="cth cth-calc" :aria-label="$t('compararPreciosProveedor.colCalculo')"></th>
                <th class="cth cth-archivo">{{ $t("compararPreciosProveedor.colExcel") }}</th>
                <th class="cth cth-producto">{{ $t("compararPreciosProveedor.colProductoDeposito") }}</th>
                <th class="cth cth-num">{{ $t("compararPreciosProveedor.colPrecioActual") }}</th>
                <th class="cth cth-num">{{ $t("compararPreciosProveedor.colDescuentoActual") }}</th>
                <th class="cth cth-num">{{ $t("compararPreciosProveedor.colPrecioVentaActual") }}</th>
                <th class="cth cth-num">{{ $t("compararPreciosProveedor.colPrecioNuevo") }}</th>
                <th class="cth cth-num">{{ $t("compararPreciosProveedor.colDescuentoNuevo") }}</th>
                <th class="cth cth-num">{{ $t("compararPreciosProveedor.colPrecioVentaNuevo") }}</th>
                <th class="cth cth-dif">{{ $t("compararPreciosProveedor.colDiferencia") }}</th>
                <th class="cth cth-accion">{{ $t("compararPreciosProveedor.colAccion") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in itemsConProducto"
                :key="index"
                class="ccp-row"
                :class="{
                  'ccp-row--asociar':  getProductoParaFila(item, index) && acciones[index] === 'asociar',
                  'ccp-row--ignorar':  getProductoParaFila(item, index) && acciones[index] === 'ignorar',
                  'ccp-row--pendiente': !getProductoParaFila(item, index),
                }"
              >
                <!-- Estrella recomendado -->
                <td class="ctd ctd-star">
                  <v-icon v-if="item.recomendado" small color="primary">mdi-star</v-icon>
                </td>

                <!-- Botón cálculo -->
                <td class="ctd ctd-calc">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <button
                        class="ccp-calc-btn"
                        :aria-label="$t('compararPreciosProveedor.tooltipCalculoFila')"
                        v-bind="attrs"
                        v-on="on"
                        @click="abrirModalCalculoFila(index)"
                      >
                        <v-icon small>mdi-calculator-variant-outline</v-icon>
                      </button>
                    </template>
                    <span>{{ $t("compararPreciosProveedor.tooltipCalculoFila") }}</span>
                  </v-tooltip>
                </td>

                <!-- En archivo -->
                <td class="ctd ctd-archivo">
                  <div class="archivo-nombre">{{ item.nombreArchivo || "—" }}</div>
                  <div v-if="item.codigoBarrasProveedor" class="archivo-codigo">{{ item.codigoBarrasProveedor }}</div>
                </td>

                <!-- Producto depósito -->
                <td class="ctd ctd-producto">
                  <v-autocomplete
                    v-if="depositoId"
                    :value="getProductoParaFila(item, index)"
                    :items="getItemsAutocomplete(index, item)"
                    :loading="loadingBusqueda[index]"
                    item-text="nombre"
                    item-value="id"
                    hide-no-data
                    dense
                    hide-details
                    solo
                    flat
                    clearable
                    return-object
                    :placeholder="$t('compararPreciosProveedor.placeholderBuscarProducto')"
                    class="table-input-producto comparar-producto-deposito pl-0 mt-0 pt-0"
                    @input="onSeleccionManual(index, $event)"
                    @update:search-input="onSearchProducto(index, $event)"
                  >
                    <template v-slot:selection="{ item: sel }">{{ getProductoNombre(sel) }}</template>
                    <template v-slot:item="{ item: row }">
                      {{ getProductoNombre(row) }}
                      <span v-if="row.codigoBarras" class="caption grey--text ml-1">({{ row.codigoBarras }})</span>
                    </template>
                  </v-autocomplete>
                  <span v-else class="caption grey--text">{{ $t("compararPreciosProveedor.sinMatchSeleccionar") }}</span>
                </td>

                <!-- Precio compra actual -->
                <td class="ctd ctd-num">
                  <span v-if="item.precioCompraActual != null">${{ formatNum(item.precioCompraActual) }}</span>
                  <span v-else class="nd">—</span>
                </td>

                <!-- Descuento actual -->
                <td class="ctd ctd-num">
                  <span v-if="item.descuentoActual != null">{{ formatNum(item.descuentoActual) }}%</span>
                  <span v-else class="nd">—</span>
                </td>

                <!-- PV actual -->
                <td class="ctd ctd-num">
                  <span v-if="getPrecioVentaActualMostrado(item, index) != null">${{ formatNum(getPrecioVentaActualMostrado(item, index)) }}</span>
                  <span v-else class="nd">—</span>
                </td>

                <!-- Precio compra nuevo -->
                <td class="ctd ctd-num">${{ formatNum(item.precioCompraNuevo) }}</td>

                <!-- Descuento nuevo -->
                <td class="ctd ctd-num">{{ formatNum(item.descuentoNuevo != null ? item.descuentoNuevo : 0) }}%</td>

                <!-- PV nuevo -->
                <td class="ctd ctd-num">
                  <span v-if="getPrecioVentaNuevoMostrado(item, index) != null">${{ formatNum(getPrecioVentaNuevoMostrado(item, index)) }}</span>
                  <span v-else class="nd">—</span>
                </td>

                <!-- Diferencia -->
                <td class="ctd ctd-dif">
                  <span
                    v-if="item.diferenciaPorcentaje != null"
                    class="dif-badge"
                    :class="{
                      'dif-badge--sube':  item.diferenciaPorcentaje > 0,
                      'dif-badge--baja':  item.diferenciaPorcentaje < 0,
                      'dif-badge--igual': item.diferenciaPorcentaje === 0,
                    }"
                  >{{ formatDiferencia(item.diferenciaPorcentaje) }}</span>
                  <span v-else class="nd">—</span>
                </td>

                <!-- Acciones -->
                <td class="ctd ctd-accion">
                  <template v-if="getProductoParaFila(item, index)">
                    <div class="accion-group">
                      <button
                        class="ccp-accion-btn"
                        :class="{ 'ccp-accion-btn--active': acciones[index] === 'ignorar' }"
                        @click="setAccion(index, 'ignorar')"
                      >
                        {{ $t("compararPreciosProveedor.ignorar") }}
                      </button>
                      <button
                        class="ccp-accion-btn ccp-accion-btn--primary"
                        :class="{ 'ccp-accion-btn--active': acciones[index] === 'asociar' }"
                        @click="setAccion(index, 'asociar')"
                      >
                        {{ $t("compararPreciosProveedor.asociar") }}
                      </button>
                    </div>
                  </template>
                  <span v-else class="nd caption">{{ $t("compararPreciosProveedor.sinMatch") }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="table-fade-right"></div>
      </div>

    </template>

    <!-- ════════ MODAL CALCULO PRECIOS ════════ -->
    <v-dialog v-model="modalCalculoPrecios" max-width="720">
      <v-card style="border-radius: 14px;">
        <v-card-title class="pb-2 dialog-calculo-precios__title d-flex align-center">
          <v-icon color="primary" class="flex-shrink-0 mr-3 dialog-calculo-precios__title-icon">
            mdi-calculator-variant-outline
          </v-icon>
          <div class="dialog-calculo-precios__title-inner headline d-flex flex-wrap align-baseline">
            <span class="dialog-calculo-precios__title-main">{{ $t("compararPreciosProveedor.modalCalculoTitulo") }}</span>
            <template v-if="etiquetaFilaCalculoModal">
              <span class="dialog-calculo-precios__title-sep grey--text text--darken-1 mx-1">—</span>
              <span class="dialog-calculo-precios__title-product">{{ etiquetaFilaCalculoModal }}</span>
            </template>
          </div>
        </v-card-title>
        <v-card-text class="pt-2 pb-0">
          <v-alert
            v-if="modalCalculoData && modalCalculoData.sinProducto"
            type="info" dense text border="left" colored-border class="mb-0"
          >
            {{ $t("compararPreciosProveedor.modalCalculoSinProducto") }}
          </v-alert>

          <v-row v-else-if="modalCalculoData" dense class="dialog-calculo-precios__row">
            <v-col cols="12" class="pb-3">
              <v-sheet class="config-producto-strip rounded-lg pa-4" outlined>
                <div class="config-producto-strip__titulo subtitle-2 font-weight-bold blue-grey--text text--darken-2 mb-3">
                  <v-icon small left color="blue-grey">mdi-tune-variant</v-icon>
                  {{ $t("compararPreciosProveedor.modalConfigProductoTitulo") }}
                </div>
                <div class="config-producto-strip__grid">
                  <div class="config-producto-strip__item">
                    <div class="config-producto-strip__label caption grey--text text--darken-1">{{ $t("compararPreciosProveedor.modalConfigIva") }}</div>
                    <div class="config-producto-strip__valor body-1 font-weight-bold">{{ formatNum(modalCalculoData.ivaProducto) }}%</div>
                  </div>
                  <div class="config-producto-strip__item">
                    <div class="config-producto-strip__label caption grey--text text--darken-1">{{ $t("compararPreciosProveedor.modalConfigMargen") }}</div>
                    <div class="config-producto-strip__valor body-1 font-weight-bold">{{ formatNum(modalCalculoData.margenProducto) }}%</div>
                  </div>
                  <div class="config-producto-strip__item config-producto-strip__item--wide">
                    <div class="config-producto-strip__label caption grey--text text--darken-1">{{ $t("compararPreciosProveedor.modalConfigRedondeo") }}</div>
                    <div class="config-producto-strip__valor body-2 font-weight-medium">{{ etiquetaRedondeo(modalCalculoData.tipoRedondeoProducto) }}</div>
                  </div>
                </div>
                <p class="config-producto-strip__ayuda caption grey--text mb-0 mt-3">{{ $t("compararPreciosProveedor.modalConfigAyuda") }}</p>
              </v-sheet>
            </v-col>

            <!-- Bloque actual -->
            <v-col cols="12" md="6">
              <v-sheet class="desglose-panel desglose-panel--actual rounded-lg pa-4" elevation="1">
                <div class="desglose-panel__heading mb-3">
                  <v-icon small left color="blue darken-1">mdi-database-outline</v-icon>
                  <span class="subtitle-1 font-weight-bold blue-grey--text text--darken-2">{{ $t("compararPreciosProveedor.modalBloqueActual") }}</span>
                </div>
                <template v-if="!modalCalculoData.actual.ok">
                  <v-alert type="warning" dense text class="mb-0 caption">{{ $t("compararPreciosProveedor.modalCalculoSinPrecioCompra") }}</v-alert>
                </template>
                <template v-else>
                  <div class="desglose-pasos">
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoPrecioCompra") }}</div>
                      <div class="desglose-paso__valor">{{ moneda(modalCalculoData.actual.precioCompra) }}</div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoDescuentoProveedor") }} <span class="desglose-paso__pct">({{ formatNum(modalCalculoData.actual.descuentoPct) }}%)</span></div>
                      <div class="desglose-paso__formula caption grey--text">{{ moneda(modalCalculoData.actual.precioCompra) }} × (1 − {{ formatNum(modalCalculoData.actual.descuentoPct) }} ÷ 100)</div>
                    </div>
                    <div class="desglose-paso desglose-paso--destacado">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoCostoNeto") }}</div>
                      <div class="desglose-paso__valor">{{ moneda(modalCalculoData.actual.neto) }}</div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoAplicarIva") }}</div>
                      <div class="desglose-paso__formula caption grey--text">{{ moneda(modalCalculoData.actual.neto) }} × (1 + {{ formatNum(modalCalculoData.actual.ivaPct) }} ÷ 100)</div>
                    </div>
                    <div class="desglose-paso desglose-paso--destacado">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoTrasIva") }}</div>
                      <div class="desglose-paso__valor">{{ moneda(modalCalculoData.actual.trasIva) }}</div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoAplicarMargen") }}</div>
                      <div class="desglose-paso__formula caption grey--text">{{ moneda(modalCalculoData.actual.trasIva) }} × (1 + {{ formatNum(modalCalculoData.actual.margenPct) }} ÷ 100)</div>
                    </div>
                    <div class="desglose-paso desglose-paso--destacado">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoAntesRedondeo") }}</div>
                      <div class="desglose-paso__valor">{{ moneda(modalCalculoData.actual.antesRedondeo) }}</div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoAplicarRedondeo") }}</div>
                      <div class="desglose-paso__detalle caption">
                        <span class="font-weight-medium">{{ etiquetaRedondeo(modalCalculoData.actual.tipoRedondeo) }}</span>
                        <span v-if="!modalCalculoData.actual.redondeoAplica" class="grey--text"> — {{ $t("compararPreciosProveedor.modalSinRedondeo") }}</span>
                      </div>
                      <div v-if="modalCalculoData.actual.redondeoAplica" class="desglose-paso__formula caption grey--text mt-1">{{ moneda(modalCalculoData.actual.antesRedondeo) }} → {{ moneda(modalCalculoData.actual.precioFinal) }}</div>
                    </div>
                    <div class="desglose-paso desglose-paso--final mt-3 pa-3 rounded-lg">
                      <div class="desglose-paso__label body-2">{{ $t("compararPreciosProveedor.modalPasoPrecioVentaFinal") }}</div>
                      <div class="desglose-paso__valor desglose-paso__valor--xl primary--text">{{ moneda(modalCalculoData.actual.precioFinal) }}</div>
                    </div>
                    <v-alert v-if="modalCalculoData.notaPrecioGuardadoActual" dense text type="info" class="mt-3 mb-0 caption">
                      {{ $t("compararPreciosProveedor.modalNotaPrecioGuardado", { monto: moneda(modalCalculoData.precioVentaGuardado) }) }}
                    </v-alert>
                  </div>
                </template>
              </v-sheet>
            </v-col>

            <!-- Bloque nuevo -->
            <v-col cols="12" md="6">
              <v-sheet
                class="desglose-panel rounded-lg pa-4"
                :class="modalCalculoData.nuevoMayorQueActual ? 'desglose-panel--nuevo-mayor' : 'desglose-panel--nuevo'"
                elevation="1"
              >
                <div class="desglose-panel__heading mb-3">
                  <v-icon small left :color="modalCalculoData.nuevoMayorQueActual ? 'red darken-2' : 'teal darken-1'">mdi-file-excel-outline</v-icon>
                  <span class="subtitle-1 font-weight-bold blue-grey--text text--darken-2">{{ $t("compararPreciosProveedor.modalBloqueNuevo") }}</span>
                </div>
                <template v-if="!modalCalculoData.nuevo.ok">
                  <v-alert type="warning" dense text class="mb-0 caption">{{ $t("compararPreciosProveedor.modalCalculoSinPrecioCompra") }}</v-alert>
                </template>
                <template v-else>
                  <div class="desglose-pasos">
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoPrecioCompra") }}</div>
                      <div class="desglose-paso__valor">{{ moneda(modalCalculoData.nuevo.precioCompra) }}</div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoDescuentoProveedor") }} <span class="desglose-paso__pct">({{ formatNum(modalCalculoData.nuevo.descuentoPct) }}%)</span></div>
                      <div class="desglose-paso__formula caption grey--text">{{ moneda(modalCalculoData.nuevo.precioCompra) }} × (1 − {{ formatNum(modalCalculoData.nuevo.descuentoPct) }} ÷ 100)</div>
                    </div>
                    <div class="desglose-paso desglose-paso--destacado">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoCostoNeto") }}</div>
                      <div class="desglose-paso__valor">{{ moneda(modalCalculoData.nuevo.neto) }}</div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoAplicarIva") }}</div>
                      <div class="desglose-paso__formula caption grey--text">{{ moneda(modalCalculoData.nuevo.neto) }} × (1 + {{ formatNum(modalCalculoData.nuevo.ivaPct) }} ÷ 100)</div>
                    </div>
                    <div class="desglose-paso desglose-paso--destacado">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoTrasIva") }}</div>
                      <div class="desglose-paso__valor">{{ moneda(modalCalculoData.nuevo.trasIva) }}</div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoAplicarMargen") }}</div>
                      <div class="desglose-paso__formula caption grey--text">{{ moneda(modalCalculoData.nuevo.trasIva) }} × (1 + {{ formatNum(modalCalculoData.nuevo.margenPct) }} ÷ 100)</div>
                    </div>
                    <div class="desglose-paso desglose-paso--destacado">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoAntesRedondeo") }}</div>
                      <div class="desglose-paso__valor">{{ moneda(modalCalculoData.nuevo.antesRedondeo) }}</div>
                    </div>
                    <v-divider class="my-2" />
                    <div class="desglose-paso">
                      <div class="desglose-paso__label">{{ $t("compararPreciosProveedor.modalPasoAplicarRedondeo") }}</div>
                      <div class="desglose-paso__detalle caption">
                        <span class="font-weight-medium">{{ etiquetaRedondeo(modalCalculoData.nuevo.tipoRedondeo) }}</span>
                        <span v-if="!modalCalculoData.nuevo.redondeoAplica" class="grey--text"> — {{ $t("compararPreciosProveedor.modalSinRedondeo") }}</span>
                      </div>
                      <div v-if="modalCalculoData.nuevo.redondeoAplica" class="desglose-paso__formula caption grey--text mt-1">{{ moneda(modalCalculoData.nuevo.antesRedondeo) }} → {{ moneda(modalCalculoData.nuevo.precioFinal) }}</div>
                    </div>
                    <div class="desglose-paso desglose-paso--final mt-3 pa-3 rounded-lg">
                      <div class="desglose-paso__label body-2">{{ $t("compararPreciosProveedor.modalPasoPrecioVentaFinal") }}</div>
                      <div
                        class="desglose-paso__valor desglose-paso__valor--xl"
                        :class="modalCalculoData.nuevoMayorQueActual ? 'error--text' : 'teal--text text--darken-2'"
                      >{{ moneda(modalCalculoData.nuevo.precioFinal) }}</div>
                    </div>
                  </div>
                </template>
              </v-sheet>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pt-2 pb-4 px-4">
          <v-spacer />
          <v-btn text color="primary" @click="cerrarModalCalculoFila">{{ $t("compararPreciosProveedor.cerrar") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import ProductoProvider from "../../../producto/providers/ProductoProvider";

export default {
  name: "PasoResultadoAnalisis",
  props: {
    items:      { type: Array,  default: () => [] },
    depositoId: { type: String, default: null },
    modo:       { type: String, default: "SIMPLE" },
  },
  data() {
    return {
      acciones: {},
      seleccionManual: {},
      productosBusqueda: {},
      loadingBusqueda: {},
      searchTimeouts: {},
      modalCalculoPrecios: false,
      filaCalculoIndex: null,
    };
  },
  computed: {
    itemsConProducto() { return this.items || []; },
    etiquetaFilaCalculoModal() {
      if (this.filaCalculoIndex == null) return "";
      return this.getNombreCalculoFila(this.filaCalculoIndex);
    },
    cantidadAsociar() {
      return this.itemsConProducto.filter((_, i) => {
        const prod = this.getProductoParaFila(this.itemsConProducto[i], i);
        return prod && this.acciones[i] === "asociar";
      }).length;
    },
    modalCalculoData() {
      const idx = this.filaCalculoIndex;
      if (idx == null) return null;
      const item = this.itemsConProducto[idx];
      if (!item) return null;
      const producto = this.getProductoParaFila(item, idx);
      if (!producto) return { sinProducto: true };
      const descActual = item.descuentoActual != null ? Number(item.descuentoActual) : 0;
      const descNuevo  = item.descuentoNuevo  != null ? Number(item.descuentoNuevo)  : 0;
      const actual = this.armarDesglose(producto, item.precioCompraActual, descActual);
      const nuevo  = this.armarDesglose(producto, item.precioCompraNuevo,  descNuevo);
      let precioVentaGuardado = null;
      const pvRaw = producto.precio != null ? producto.precio : producto.precioVenta;
      if (pvRaw != null && pvRaw !== "") {
        const pv = Number(pvRaw);
        if (!Number.isNaN(pv)) precioVentaGuardado = pv;
      }
      let notaPrecioGuardadoActual = false;
      if (actual.ok && precioVentaGuardado != null) {
        notaPrecioGuardadoActual = Math.abs(precioVentaGuardado - actual.precioFinal) > 0.015;
      }
      const nuevoMayorQueActual = actual.ok && nuevo.ok && Number(nuevo.precioFinal) > Number(actual.precioFinal) + 0.005;
      return {
        sinProducto: false,
        actual,
        nuevo,
        precioVentaGuardado,
        notaPrecioGuardadoActual,
        nuevoMayorQueActual,
        ivaProducto:          Number(producto.ivaPorcentaje) || 0,
        margenProducto:       Number(producto.porcentajeSobreCosto) || 0,
        tipoRedondeoProducto: producto.tipoRedondeo || "ninguno",
      };
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          const next = {};
          val.forEach((item, i) => {
            next[i] = item.producto && item.recomendado ? "asociar" : "ignorar";
          });
          this.acciones = next;
          this.seleccionManual = {};
          this.$nextTick(() => this.emitCantidadParaAplicar());
        }
      },
    },
    modalCalculoPrecios(val) {
      if (!val) this.filaCalculoIndex = null;
    },
  },
  methods: {
    abrirModalCalculoFila(index) {
      this.filaCalculoIndex = index;
      this.modalCalculoPrecios = true;
    },
    cerrarModalCalculoFila() { this.modalCalculoPrecios = false; },
    getNombreCalculoFila(index) {
      const item = this.itemsConProducto[index];
      if (!item) return "—";
      const p = this.getProductoParaFila(item, index);
      if (p) return this.getProductoNombre(p);
      return item.nombreArchivo || item.codigoBarrasProveedor || "—";
    },
    getProductoParaFila(item, index) {
      if (Object.prototype.hasOwnProperty.call(this.seleccionManual, index)) {
        return this.seleccionManual[index] || null;
      }
      return (item && item.producto) || null;
    },
    getItemsAutocomplete(index, item) {
      const search = this.productosBusqueda[index] || [];
      const sel = this.getProductoParaFila(item, index);
      if (!sel) return search;
      const sid = String(sel.id || sel._id || "");
      const exists = search.some((p) => String(p.id || p._id || "") === sid);
      if (exists) return search;
      return [sel, ...search];
    },
    getProductoNombre(p) {
      if (!p) return "—";
      const tieneMarcaModeloTamanio =
        p.marca && p.marca.nombre && p.modelo && p.modelo.nombre && p.tamanio && p.tamanio.tamanio;
      if (this.modo === "ESTRUCTURADO" || tieneMarcaModeloTamanio) {
        const parts = [
          p.marca  && p.marca.nombre,
          p.modelo && p.modelo.nombre,
          p.tamanio && p.tamanio.tamanio,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(" ") : p.nombre || p.codigoBarras || p.id || "—";
      }
      return p.nombre || p.codigoBarras || p.id || "—";
    },
    armarDesglose(producto, precioCompra, descuentoPct) {
      const pc = precioCompra != null ? Number(precioCompra) : NaN;
      const d  = Number(descuentoPct) || 0;
      if (!producto || Number.isNaN(pc) || pc <= 0) return { ok: false };
      const iva    = Number(producto.ivaPorcentaje) || 0;
      const margen = Number(producto.porcentajeSobreCosto) || 0;
      const tr     = producto.tipoRedondeo || "ninguno";
      const neto   = pc * (1 - d / 100);
      const trasIva = neto * (1 + iva / 100);
      const antesRedondeo = trasIva * (1 + margen / 100);
      let final = antesRedondeo;
      if (final > 0 && tr !== "ninguno") {
        switch (tr) {
          case "centavos":     final = Math.ceil(antesRedondeo);       break;
          case "multiplo_10":  final = Math.ceil(antesRedondeo / 10) * 10;   break;
          case "multiplo_100": final = Math.ceil(antesRedondeo / 100) * 100; break;
        }
      }
      final = parseFloat(final.toFixed(2));
      return { ok: true, precioCompra: pc, descuentoPct: d, neto, ivaPct: iva, trasIva, margenPct: margen, antesRedondeo, tipoRedondeo: tr, precioFinal: final, redondeoAplica: tr !== "ninguno" && antesRedondeo > 0 };
    },
    calcularPrecioVentaDesdeCompra(precioCompra, descuentoProv, producto) {
      const res = this.armarDesglose(producto, precioCompra, descuentoProv);
      return res.ok ? res.precioFinal : null;
    },
    moneda(n) {
      if (n == null || Number.isNaN(Number(n))) return "—";
      return `$${Number(n).toFixed(2)}`;
    },
    etiquetaRedondeo(tr) {
      const map = { ninguno: "redondeoNinguno", centavos: "redondeoCentavos", multiplo_10: "redondeoMultiplo10", multiplo_100: "redondeoMultiplo100" };
      return this.$t(`compararPreciosProveedor.${map[tr] || "redondeoNinguno"}`);
    },
    getPrecioVentaActualMostrado(item, index) {
      const p = this.getProductoParaFila(item, index);
      const v = p && (p.precio != null ? p.precio : p.precioVenta);
      if (v == null || v === "") return null;
      return Number(v);
    },
    getPrecioVentaNuevoMostrado(item, index) {
      const p = this.getProductoParaFila(item, index);
      if (!p) return null;
      return this.calcularPrecioVentaDesdeCompra(item.precioCompraNuevo, item.descuentoNuevo != null ? item.descuentoNuevo : 0, p);
    },
    formatNum(n)        { return Number(n).toFixed(2); },
    formatDiferencia(pct) {
      const n    = Number(pct);
      const sign = n >= 0 ? "+" : "";
      return `${sign}${n.toFixed(1)}%`;
    },
    setAccion(index, accion) {
      this.$set(this.acciones, index, accion);
      this.$nextTick(() => this.emitCantidadParaAplicar());
    },
    onSeleccionManual(index, selected) {
      if (selected && typeof selected === "object") {
        this.$set(this.seleccionManual, index, { ...selected, id: selected.id || selected._id });
        this.$set(this.acciones, index, "asociar");
      } else {
        this.$set(this.seleccionManual, index, null);
        this.$set(this.acciones, index, "ignorar");
      }
      this.$nextTick(() => this.emitCantidadParaAplicar());
    },
    onSearchProducto(index, e) {
      const q = typeof e === "string" ? e.trim() : (e && e.target && e.target.value) ? String(e.target.value).trim() : "";
      if (this.searchTimeouts[index]) clearTimeout(this.searchTimeouts[index]);
      if (!q || q.length < 2) { this.$set(this.productosBusqueda, index, []); return; }
      this.searchTimeouts[index] = setTimeout(() => {
        this.$set(this.loadingBusqueda, index, true);
        ProductoProvider.searchProductosByMarcaModelo(q, this.depositoId)
          .then((res) => {
            const items = (res && res.data && res.data.productoSearchByMarcaModelo) || [];
            this.$set(this.productosBusqueda, index, items.map((p) => ({ ...p, id: p.id || p._id })));
          })
          .catch(() => this.$set(this.productosBusqueda, index, []))
          .finally(() => this.$set(this.loadingBusqueda, index, false));
      }, 300);
    },
    emitCantidadParaAplicar() {
      this.$emit("update:cantidadParaAplicar", this.getItemsParaAplicar().length);
    },
    getItemsParaAplicar() {
      return this.itemsConProducto
        .map((item, index) => ({ item, index }))
        .filter(({ item, index }) => {
          const producto = this.getProductoParaFila(item, index);
          return producto && this.acciones[index] === "asociar";
        })
        .map(({ item, index }) => {
          const producto = this.getProductoParaFila(item, index);
          return {
            productoId:            producto.id || producto._id,
            codigoBarrasProveedor: item.codigoBarrasProveedor || "",
            precioCompra:          item.precioCompraNuevo,
            descuentoProveedor:    item.descuentoNuevo ?? 0,
          };
        });
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   EMPTY STATE
═══════════════════════════════════════ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 32px;
}
.empty-text { font-size: 13px; color: #9E9E9E; margin: 0; }

/* ═══════════════════════════════════════
   LEYENDA
═══════════════════════════════════════ */
.ccp-leyenda {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.legend-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid;
}
.legend-chip--asociar  { background: #F1F8E9; border-color: #DCEDC8; color: #2E7D32; }
.legend-chip--ignorar  { background: #FFF8F0; border-color: #FFE0B2; color: #E65100; }
.legend-chip--pendiente{ background: #FAFAFA; border-color: #E8EAED; color: #757575; }
.legend-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.legend-dot--asociar  { background: #4CAF50; }
.legend-dot--ignorar  { background: #FF9800; }
.legend-dot--pendiente{ background: #BDBDBD; }
.legend-resumen { font-size: 12px; color: #5F6368; }

/* ═══════════════════════════════════════
   TABLE WRAPPER
═══════════════════════════════════════ */
.table-wrapper {
  position: relative;
  border-top: 1px solid #E8EAED;
  overflow: hidden;
}
.table-scroll { overflow-x: auto; }
.table-fade-right {
  position: absolute; top: 0; right: 0; bottom: 0; width: 32px;
  background: linear-gradient(to right, transparent, rgba(255,255,255,0.9)); pointer-events: none;
}

/* ═══════════════════════════════════════
   TABLE
═══════════════════════════════════════ */
.ccp-table {
  border-collapse: collapse;
  width: 100%;
  min-width: 1100px;
  font-size: 12.5px;
}

.cth {
  position: sticky;
  top: 0;
  background: #F8F9FA;
  border-bottom: 2px solid #E8EAED;
  border-right: 1px solid #F0F0F0;
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #5F6368;
  white-space: nowrap;
  z-index: 1;
  text-align: left;
}
.cth:last-child { border-right: none; }

.cth-star   { width: 32px; min-width: 32px; }
.cth-calc   { width: 40px; min-width: 40px; text-align: center; }
.cth-archivo{ min-width: 160px; max-width: 220px; }
.cth-producto{ min-width: 200px; max-width: 280px; }
.cth-num    { min-width: 110px; }
.cth-dif    { min-width: 80px; }
.cth-accion { min-width: 160px; text-align: center; }

.ccp-row { transition: background 0.1s; }
.ccp-row--asociar  { background: #F1F8E9; }
.ccp-row--ignorar  { background: #FFF8F0; }
.ccp-row--pendiente{ background: #FAFAFA; }

.ctd {
  padding: 5px 10px;
  border-right: 1px solid #F0F0F0;
  border-bottom: 1px solid #F5F5F5;
  vertical-align: middle;
  color: #3C4043;
}
.ctd:last-child { border-right: none; }

.ctd-star  { text-align: center; width: 32px; }
.ctd-calc  { text-align: center; width: 40px; padding: 4px; }
.ctd-archivo { max-width: 220px; line-height: 1.3; }
.ctd-num   { white-space: nowrap; }
.ctd-dif   { white-space: nowrap; }
.ctd-accion{ text-align: center; }
.ctd-producto { min-width: 200px; max-width: 280px; }

.nd { color: #BDBDBD; }

.archivo-nombre { font-size: 12.5px; color: #5F6368; }
.archivo-codigo { font-size: 11.5px; color: #9E9E9E; margin-top: 2px; }

/* Calc button */
.ccp-calc-btn {
  background: none; border: none; cursor: pointer;
  padding: 4px 6px; border-radius: 6px; color: #5F6368;
  transition: background 0.15s, color 0.15s;
  display: inline-flex; align-items: center;
}
.ccp-calc-btn:hover { background: #EEF2FF; color: #1976D2; }

/* Difference badge */
.dif-badge {
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 4px;
  font-size: 11.5px; font-weight: 600;
}
.dif-badge--sube  { background: #FFEBEE; color: #C62828; }
.dif-badge--baja  { background: #E8F5E9; color: #2E7D32; }
.dif-badge--igual { background: #F5F5F5; color: #757575; }

/* Accion buttons */
.accion-group { display: flex; align-items: center; gap: 4px; justify-content: center; }
.ccp-accion-btn {
  background: none; border: 1px solid #E8EAED; border-radius: 5px;
  font-size: 11.5px; padding: 3px 8px; cursor: pointer; color: #5F6368;
  transition: all 0.15s; white-space: nowrap;
}
.ccp-accion-btn:hover { background: #F1F3F4; border-color: #BDBDBD; }
.ccp-accion-btn--active {
  background: #E8EAED; border-color: #9E9E9E; color: #202124; font-weight: 600;
}
.ccp-accion-btn--primary.ccp-accion-btn--active {
  background: #E3F2FD; border-color: #1976D2; color: #1565C0;
}

/* Autocomplete producto — indigo border (preserved) */
::v-deep .comparar-producto-deposito.table-input-producto .v-input__control {
  min-height: 32px !important;
}
::v-deep .comparar-producto-deposito.table-input-producto .v-input__slot {
  background-color: #ffffff !important;
  border: 2px solid #7986cb !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  margin-bottom: 0 !important;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
::v-deep .comparar-producto-deposito.table-input-producto .v-input__slot:hover {
  border-color: #5c6bc0 !important;
  box-shadow: 0 2px 6px rgba(63,81,181,0.15);
}
::v-deep .comparar-producto-deposito.table-input-producto.v-input--is-focused .v-input__slot {
  background-color: #e8eaf6 !important;
  border-color: #3f51b5 !important;
  box-shadow: 0 0 0 3px rgba(63,81,181,0.2), 0 2px 8px rgba(63,81,181,0.25);
}
::v-deep .comparar-producto-deposito.table-input-producto .v-select__selection,
::v-deep .comparar-producto-deposito.table-input-producto input {
  color: #1a237e; font-weight: 600; font-size: 0.875rem;
}
::v-deep .comparar-producto-deposito.table-input-producto .v-select__selection {
  max-width: 100%;
}

/* ═══════════════════════════════════════
   MODAL DESGLOSE (unchanged styles)
═══════════════════════════════════════ */
.dialog-calculo-precios__title { word-break: break-word; line-height: 1.35; }
.dialog-calculo-precios__title-inner {
  flex: 1 1 auto; min-width: 0; row-gap: 2px; column-gap: 0;
}
.dialog-calculo-precios__title-inner .dialog-calculo-precios__title-sep {
  font-weight: 400; user-select: none; flex-shrink: 0;
}
.dialog-calculo-precios__title-icon { align-self: center; }
.dialog-calculo-precios__row { margin-top: 0; }

.config-producto-strip { background: #fafafa; border-color: rgba(0,0,0,0.12) !important; }
.config-producto-strip__titulo { display: flex; align-items: center; }
.config-producto-strip__grid { display: flex; flex-wrap: wrap; gap: 16px 24px; align-items: flex-start; }
.config-producto-strip__item { min-width: 100px; }
.config-producto-strip__item--wide { flex: 1 1 200px; min-width: 180px; }
.config-producto-strip__label { margin-bottom: 4px; letter-spacing: 0.02em; }
.config-producto-strip__valor { font-variant-numeric: tabular-nums; line-height: 1.35; }
.config-producto-strip__ayuda { line-height: 1.4; border-top: 1px solid rgba(0,0,0,0.06); padding-top: 12px; }

.desglose-panel--actual    { background: #f7f9ff; border: 1px solid rgba(25,118,210,0.14); }
.desglose-panel--nuevo     { background: #f4fbf9; border: 1px solid rgba(0,121,107,0.14); }
.desglose-panel--nuevo-mayor { background: #fff8f7; border: 1px solid rgba(198,40,40,0.22); }
.desglose-panel__heading { display: flex; align-items: center; }
.desglose-pasos { font-size: 0.875rem; }
.desglose-paso {
  display: grid; grid-template-columns: 1fr auto; gap: 2px 10px; align-items: baseline;
}
.desglose-paso__formula, .desglose-paso__detalle { grid-column: 1 / -1; }
.desglose-paso__label { color: rgba(0,0,0,0.72); line-height: 1.35; }
.desglose-paso__valor { font-variant-numeric: tabular-nums; font-weight: 500; text-align: right; white-space: nowrap; }
.desglose-paso--destacado .desglose-paso__valor { font-weight: 600; color: rgba(0,0,0,0.87); }
.desglose-paso--final {
  background: rgba(0,0,0,0.04); border: 1px solid rgba(0,0,0,0.08); display: block;
}
.desglose-paso--final .desglose-paso__label { margin-bottom: 4px; }
.desglose-paso--final .desglose-paso__valor--xl { text-align: left; }
.desglose-paso__valor--xl { font-size: 1.35rem; font-weight: 700; letter-spacing: 0.02em; }
.desglose-paso__pct { font-weight: 400; }
</style>
